/** @format */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  Typography,
  Alert,
  CircularProgress,
  styled,
} from '@mui/material';
import { AlternateEmail, ArrowBack } from '@mui/icons-material';

import Symbol from '../../../Assets/SVG/symbol';
import img from '../../../Assets/login.jpg';
import { forgotPassword } from '../../../Services/keycloak/keycloak.service';
import { isTokenValid, getAuthToken } from '../../../Util/authUtils';

const AuthContainer = styled(Box, { label: 'authPaper' })({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 24,
  padding: 24,
  width: '340px',
  position: 'absolute',
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -70%)',
  borderRadius: '16px',
  background: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  border: '1px solid rgba(255, 255, 255, 0.18)',
  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
});

const inputSx = {
  '& .MuiInputLabel-root': { color: 'white', fontSize: 12 },
  '& .MuiOutlinedInput-root': {
    color: 'white',
    fontSize: 14,
    '& fieldset': { borderColor: 'white' },
  },
  '& .MuiFormHelperText-root': {
    color: '#ff8a80',
    fontSize: 12,
    marginLeft: 0,
  },
};

const titleSx = { color: 'white', fontSize: 14, fontWeight: 600 };
const subtitleSx = {
  color: 'rgba(255,255,255,0.75)',
  fontSize: 12,
  textAlign: 'center',
};
const alertSx = { width: '100%', fontSize: 12, '& .MuiAlert-message': { fontSize: 12 } };

function ForgotPasswordEmail() {
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({
    show: false,
    message: '',
    severity: 'info',
  });

  const navigate = useNavigate();

  useEffect(() => {
    if (isTokenValid(getAuthToken())) navigate('/', { replace: true });
  }, [navigate]);

  const validate = () => {
    const temp = {};
    if (!email) temp.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(email)) temp.email = 'Invalid email format';
    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  const handleSendResetLink = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await forgotPassword({
        email: email.trim(),
        redirectUrl: `${window.location.origin}/reset-password?token=`,
      });
      setAlert({
        show: true,
        message: 'Reset password link sent to your email',
        severity: 'success',
      });
    } catch (error) {
      setAlert({
        show: true,
        message:
          error?.response?.data?.message || 'Failed to send reset link',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      component="section"
      sx={{
        position: 'relative',
        height: '100%',
        background: `linear-gradient(#00000090,#00000090),url(${img})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <AuthContainer>
        <Box>
          <Symbol width="40" height="40" color="#fafafa" />
        </Box>
        <Typography variant="body2" sx={titleSx}>
          Forgot Password
        </Typography>
        <Typography variant="body2" sx={subtitleSx}>
          Enter your registered email and we will send you a reset link.
        </Typography>

        {alert.show && (
          <Alert severity={alert.severity} sx={alertSx}>
            {alert.message}
          </Alert>
        )}

        <Box
          component="form"
          onSubmit={handleSendResetLink}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            width: '100%',
          }}
        >
          <TextField
            size="small"
            required
            type="email"
            label="email"
            variant="outlined"
            autoComplete="off"
            value={email}
            sx={inputSx}
            helperText={errors.email || ' '}
            error={Boolean(errors.email)}
            onChange={(e) => {
              setEmail(e.target.value);
              setErrors({});
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ color: 'white' }}>
                  <AlternateEmail />
                </InputAdornment>
              ),
            }}
          />
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}
            sx={{ fontSize: 14 }}
          >
            Send Reset Link
          </Button>
          <Button
            size="small"
            startIcon={<ArrowBack sx={{ fontSize: 10}} />}
            sx={{ color: 'white', fontSize: 10, mt: '-4px' }}
            onClick={() => navigate('/login')}
          >
            Back to Login
          </Button>
        </Box>
      </AuthContainer>
    </Box>
  );
}

export default ForgotPasswordEmail;
